import {
  IconCheckOutline18,
  IconChevronDownOutline18,
  IconChevronRightOutline18,
  IconPlugOutline18,
} from "nucleo-ui-outline-18";
import { Input } from "@/components/ui/input";
import { SlackOAuthConnectButton } from "@/app/(v2)/connectors/_components/SlackOAuthConnectButton";
import { useConnectorsStore } from "@/stores/connectorsStore";
import {
  fetchSlackChannelById,
  fetchSlackChannels,
  isSlackId,
  SLACK_ID_PATTERN,
  slackUserToChannel,
  type SlackChannel,
} from "./slackChannels";
import { fetchSlackUsers } from "./slackUsers";
import { stripSlackChannelMetadata } from "@/utils/parseAskBlock";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PixelLoader } from "@/components/ui/pixel-loader";

export interface ResolvedSlackSelection {
  channelId: string;
  channelName?: string;
  workspaceId?: string;
  workspaceName?: string;
}

interface SlackChannelSelectorProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  required?: boolean;
  /** Optional: pre-select a specific connector. If omitted, the selector manages workspace selection internally. */
  connectorId?: string;
  /** Optional: connector type. Defaults to "slack". */
  connectorType?: string;
  multiSelect?: boolean;
  /** Called when the active workspace connector changes (for syncing with parent state). */
  onConnectorChange?: (connectorId: string) => void;
  /** Called whenever the resolved selection (workspace + channel names) is known. */
  onResolvedChange?: (selections: ResolvedSlackSelection[]) => void;
  /** When true, render the current selection as static text (no interactive dropdowns). */
  disabled?: boolean;
}

function getChannelTypeLabel(channel: SlackChannel): string {
  if (channel.is_user) return "User";
  if (channel.is_im) return "DM";
  if (channel.is_mpim) return "Group DM";
  if (channel.is_private || channel.is_group) return "Private";
  return "Public";
}

function getChannelDisplayName(channel: SlackChannel): string {
  return channel.is_user ? `@${channel.name}` : `#${channel.name}`;
}

/**
 * Render the content of the "Use channel ID" dropdown affordance based on
 * the current lookup state. Extracted to keep the JSX readable — we have
 * five mutually exclusive states (loading / resolved / not-found / error /
 * default) and nesting that many ternaries in-place violates the project
 * style rule against nested ternaries.
 */
function renderIdLookupRow(args: {
  id: string;
  lookupLoading: boolean;
  resolvedChannel: SlackChannel | undefined;
  lookupState: "idle" | "not_found" | "error";
  lookupErrorMessage: string | null;
}): ReactNode {
  const { id, lookupLoading, resolvedChannel, lookupState, lookupErrorMessage } = args;
  if (lookupLoading) {
    return (
      <span className="text-ds-text-muted flex items-center gap-2">
        <PixelLoader size="sm" />
        Looking up {id}...
      </span>
    );
  }
  if (resolvedChannel) {
    return (
      <>
        <span className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-700 shrink-0 rounded px-1.5 py-0.5 text-xs">
          {getChannelTypeLabel(resolvedChannel)}
        </span>
        <span className="text-ds-text-primary min-w-0 flex-1 truncate">{getChannelDisplayName(resolvedChannel)}</span>
        {!resolvedChannel.is_user && resolvedChannel.num_members != null && (
          <span className="caption-xs shrink-0">{resolvedChannel.num_members} members</span>
        )}
        {/* conversations.info returns is_member=false when the bot can see
            the channel metadata but isn't a member. Private channels fail
            at send time; public channels only succeed if the bot has
            chat:write.public. Warn in both cases — a false positive for
            properly-scoped bots is better than silent send failures. */}
        {!resolvedChannel.is_user && resolvedChannel.is_member === false && (
          <span className="shrink-0 text-xs text-amber-600 dark:text-amber-400">
            (bot not a member — invite the bot to send)
          </span>
        )}
      </>
    );
  }
  if (lookupState === "not_found") {
    return (
      <span className="text-ds-text-muted">
        Use channel ID: {id} <span className="text-amber-600 dark:text-amber-400">(not found or bot not a member)</span>
      </span>
    );
  }
  if (lookupState === "error") {
    return (
      <span className="text-ds-text-muted">
        Use channel ID: {id}{" "}
        <span className="text-red-600 dark:text-red-400">
          (lookup failed{lookupErrorMessage ? ` — ${lookupErrorMessage}` : ""})
        </span>
      </span>
    );
  }
  return <span className="text-blue-600 dark:text-blue-400">Use channel ID: {id}</span>;
}

export default function SlackChannelSelector({
  label,
  description,
  headerRight,
  value,
  onChange,
  required,
  connectorId: externalConnectorId,
  connectorType: externalConnectorType,
  multiSelect = false,
  onConnectorChange,
  onResolvedChange,
  disabled = false,
}: SlackChannelSelectorProps) {
  // --- Workspace selection (internal) ---
  const connectors = useConnectorsStore(s => s.connectors);
  const loadConnectors = useConnectorsStore(s => s.loadConnectors);

  const slackConnectors = useMemo(() => connectors.filter(c => c.type === "slack"), [connectors]);

  // If external connectorId is provided, use it. Otherwise manage internally.
  const [internalConnectorId, setInternalConnectorId] = useState<string | null>(null);
  const [workspaceDropdownOpen, setWorkspaceDropdownOpen] = useState(false);
  const workspaceRef = useRef<HTMLDivElement>(null);

  // Resolve active connector
  const activeConnectorId = externalConnectorId || internalConnectorId;
  const activeConnector = useMemo(
    () => slackConnectors.find(c => c.id === activeConnectorId),
    [slackConnectors, activeConnectorId],
  );
  const activeConnectorType = externalConnectorType || activeConnector?.type || "slack";

  // Auto-select when there's exactly one workspace, and clear stale connector IDs
  useEffect(() => {
    if (externalConnectorId) return;
    const isStale = internalConnectorId && !slackConnectors.some(c => c.id === internalConnectorId);
    if (slackConnectors.length === 1 && (!internalConnectorId || isStale)) {
      const id = slackConnectors[0].id;
      setInternalConnectorId(id);
      onConnectorChange?.(id);
    } else if (isStale) {
      setInternalConnectorId(null);
    }
  }, [externalConnectorId, slackConnectors, internalConnectorId, onConnectorChange]);

  // Close workspace dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (workspaceRef.current && !workspaceRef.current.contains(e.target as Node)) {
        setWorkspaceDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleWorkspaceChange = useCallback(
    (id: string) => {
      setInternalConnectorId(id);
      setWorkspaceDropdownOpen(false);
      setManualMode(false);
      onConnectorChange?.(id);
      // Clear channel selection when workspace changes
      onChange(multiSelect ? [] : "");
    },
    [onChange, multiSelect, onConnectorChange],
  );

  const handleSlackConnected = useCallback(() => {
    void loadConnectors();
  }, [loadConnectors]);

  // --- Channel selection ---
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const channelCacheRef = useRef<Map<string, SlackChannel[]>>(new Map());
  // In-flight slack-get-channel-info requests, keyed by channel ID. Shared
  // across re-renders so the mount-time batch resolver doesn't re-fire the
  // same lookup when `selectedValues` identity changes (StrictMode, parent
  // re-renders). Each promise is cleared from the map when it settles.
  const inflightLookupRef = useRef<Map<string, Promise<void>>>(new Map());

  // Memoize so single-select mode (value: string) doesn't allocate a fresh
  // array on every render. A new reference each render would make any
  // useEffect that depends on selectedValues re-fire, which in turn would
  // kick off redundant conversations.info requests during mount/StrictMode.
  const selectedValues: string[] = useMemo(() => (Array.isArray(value) ? value : value ? [value] : []), [value]);

  // Fetch channels when active connector changes
  useEffect(() => {
    if (!activeConnectorId || !activeConnectorType) {
      setChannels([]);
      return;
    }

    const cacheKey = `${activeConnectorId}:${activeConnectorType}`;
    const cached = channelCacheRef.current.get(cacheKey);
    if (cached) {
      setChannels(cached);
      setManualMode(false);
      setLoading(false);
      return;
    }

    setManualMode(false);
    let cancelled = false;

    async function fetchChannelsAsync() {
      setLoading(true);
      setFetchError(false);
      setChannels([]);
      try {
        // Fetch public channels and workspace users in parallel. Users are
        // surfaced as "channel-like" entries so the selector supports
        // name-based search for DM targets (private channels remain hidden
        // by design — they require pasting the channel ID).
        const [channelList, userList] = await Promise.all([
          fetchSlackChannels(activeConnectorId!, activeConnectorType!),
          fetchSlackUsers(activeConnectorId!, activeConnectorType!).catch(err => {
            // Users are nice-to-have — if the workspace lacks users:read
            // or the call fails, fall back to channels-only. Log so the
            // failure mode is visible in devtools.
            console.warn("[SlackChannelSelector] fetchSlackUsers failed", err);
            return [];
          }),
        ]);
        if (!cancelled) {
          const userChannels = userList.map(slackUserToChannel).filter((ch): ch is SlackChannel => ch !== null);
          const combined = [...channelList, ...userChannels];
          setChannels(combined);
          setManualMode(false);
          channelCacheRef.current.set(cacheKey, combined);
        }
      } catch {
        if (!cancelled) {
          setFetchError(true);
          setManualMode(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchChannelsAsync();
    return () => {
      cancelled = true;
    };
  }, [activeConnectorId, activeConnectorType]);

  // Close channel dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node) &&
        !workspaceRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const trimmedQuery = query.trim();
  // Drop a leading # or @ so both "#general" / "@trung" style queries work
  // the same as the bare name.
  const normalizedQuery = trimmedQuery.replace(/^[#@]/, "");
  const lowerQuery = normalizedQuery.toLowerCase();
  const upperQuery = normalizedQuery.toUpperCase();

  // Precompute lowercase names once per `channels` change — keeps the
  // per-keystroke filter cost down when the workspace has thousands of
  // channels + users combined.
  const channelsWithLower = useMemo(() => channels.map(ch => ({ ch, lower: ch.name.toLowerCase() })), [channels]);

  const filteredChannels = useMemo(() => {
    if (!normalizedQuery) return channels;
    return channelsWithLower
      .filter(({ ch, lower }) => lower.includes(lowerQuery) || ch.id === upperQuery)
      .map(x => x.ch);
  }, [channels, channelsWithLower, normalizedQuery, lowerQuery, upperQuery]);

  const queryIsChannelId = isSlackId(normalizedQuery);

  // Lookup-by-ID: when the user pastes a channel ID that's not in the visible
  // list (private channels aren't listed — see slackChannels.ts), resolve it
  // via conversations.info so we can show the real name + type in the dropdown
  // and in any selected-channel tags.
  const [resolvedById, setResolvedById] = useState<Map<string, SlackChannel>>(() => new Map());
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupState, setLookupState] = useState<"idle" | "not_found" | "error">("idle");
  const [lookupErrorMessage, setLookupErrorMessage] = useState<string | null>(null);

  const channelsById = useMemo(() => new Map(channels.map(ch => [ch.id, ch])), [channels]);
  const channelIdAlreadyInList = queryIsChannelId && channelsById.has(upperQuery);
  const findChannel = useCallback(
    (id: string) => channelsById.get(id) ?? resolvedById.get(id),
    [channelsById, resolvedById],
  );

  // Debounced channel-ID lookup when the user types a valid-looking ID.
  useEffect(() => {
    if (!activeConnectorId || !queryIsChannelId) {
      setLookupLoading(false);
      setLookupState("idle");
      setLookupErrorMessage(null);
      return;
    }
    const idUpper = upperQuery;
    if (channelsById.has(idUpper) || resolvedById.has(idUpper)) {
      setLookupLoading(false);
      setLookupState("idle");
      setLookupErrorMessage(null);
      return;
    }
    let cancelled = false;
    setLookupLoading(true);
    setLookupState("idle");
    setLookupErrorMessage(null);
    const handle = setTimeout(async () => {
      const result = await fetchSlackChannelById(activeConnectorId, activeConnectorType, idUpper);
      if (cancelled) return;
      if (result.status === "found") {
        setResolvedById(prev => {
          if (prev.has(result.channel.id)) return prev;
          const next = new Map(prev);
          next.set(result.channel.id, result.channel);
          return next;
        });
      } else if (result.status === "not_found") {
        setLookupState("not_found");
      } else {
        setLookupState("error");
        setLookupErrorMessage(result.message);
      }
      setLookupLoading(false);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [activeConnectorId, activeConnectorType, queryIsChannelId, upperQuery, channelsById, resolvedById]);

  const resolvedQueryChannel = queryIsChannelId ? resolvedById.get(upperQuery) : undefined;

  // IDs the batch resolver confirmed as benign misses (not_found). Only
  // populated on confirmed absence — NOT on transport/auth errors — so a
  // rate-limited or revoked-token bounce doesn't permanently hide real
  // channels. Cleared when the active connector changes (reconnect) so a
  // fresh token can re-resolve previously-failed IDs.
  const missedLookupIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    missedLookupIdsRef.current.clear();
    inflightLookupRef.current.clear();
  }, [activeConnectorId]);

  // Resolve any already-selected channel IDs that aren't in the fetched list
  // (typical when the selector opens an existing node that targets a private
  // channel). Without this the selected tag renders as "Channel: C01ABC..."
  // instead of the friendly name.
  useEffect(() => {
    if (!activeConnectorId || selectedValues.length === 0) return;
    const toResolve = selectedValues
      .map(v => v.trim().toUpperCase())
      .filter(
        id =>
          SLACK_ID_PATTERN.test(id) &&
          !channelsById.has(id) &&
          !resolvedById.has(id) &&
          !missedLookupIdsRef.current.has(id),
      );
    if (toResolve.length === 0) return;
    let cancelled = false;
    const inflight = inflightLookupRef.current;
    const lookupOnce = (id: string): Promise<void> => {
      const existing = inflight.get(id);
      if (existing) return existing;
      const p = fetchSlackChannelById(activeConnectorId, activeConnectorType, id).then(result => {
        if (result.status === "found") {
          setResolvedById(prev => {
            if (prev.has(result.channel.id)) return prev;
            const next = new Map(prev);
            next.set(result.channel.id, result.channel);
            return next;
          });
        } else if (result.status === "not_found") {
          // Only benign misses blacklist — a real channel_not_found / not_in_channel
          // / archived response won't change until the bot's membership does.
          // Transport/auth errors (invalid_auth, token_revoked, ratelimited,
          // HTTP 5xx) are transient; retrying them on the next render is
          // preferable to permanently showing bare IDs until the user nav's
          // away and back.
          missedLookupIdsRef.current.add(id);
        }
      });
      const tracked = p.finally(() => {
        inflight.delete(id);
      });
      inflight.set(id, tracked);
      return tracked;
    };
    (async () => {
      // Cap concurrency so a large multi-select doesn't hammer Slack's
      // conversations.info rate limit (Tier 3 = ~50/min/workspace).
      const MAX_CONCURRENCY = 4;
      for (let i = 0; i < toResolve.length && !cancelled; i += MAX_CONCURRENCY) {
        const batch = toResolve.slice(i, i + MAX_CONCURRENCY);
        await Promise.all(batch.map(lookupOnce));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeConnectorId, activeConnectorType, selectedValues, channelsById, resolvedById]);

  // Notify parent of the resolved channel/workspace details whenever any relevant
  // piece (value, channel list, active workspace) changes. Skip the empty case
  // on mount to avoid a spurious parent state update.
  useEffect(() => {
    if (!onResolvedChange) return;
    if (selectedValues.length === 0) return;
    const resolved: ResolvedSlackSelection[] = selectedValues.map(id => {
      const ch = findChannel(id);
      return {
        channelId: id,
        channelName: ch?.name,
        workspaceId: activeConnectorId || undefined,
        workspaceName: activeConnector?.name,
      };
    });
    onResolvedChange(resolved);
    // Depend on string keys so unchanged selections don't refire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues.join(","), channelsById, resolvedById, activeConnectorId, activeConnector?.name]);

  const handleSelect = (channelId: string) => {
    if (multiSelect) {
      const current = [...selectedValues];
      const idx = current.indexOf(channelId);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(channelId);
      }
      onChange(current);
    } else {
      onChange(channelId);
      setIsOpen(false);
      setQuery("");
    }
  };

  const handleRemove = (channelId: string) => {
    if (multiSelect) {
      onChange(selectedValues.filter(v => v !== channelId));
    } else {
      onChange("");
    }
  };

  // --- Render: No Slack workspaces connected ---
  if (!externalConnectorId && slackConnectors.length === 0) {
    return (
      <div className="space-y-2">
        {label && (
          <div className="flex items-center justify-between gap-3">
            <label className="field-label block">
              {label}
              {required && <span className="ml-1 text-red-500">*</span>}
            </label>
            {headerRight}
          </div>
        )}
        {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
        <SlackOAuthConnectButton onSuccess={handleSlackConnected} />
      </div>
    );
  }

  // --- Render: Workspace selector (only when multiple workspaces and no external connector) ---
  const showWorkspaceSelector = !externalConnectorId && slackConnectors.length > 1;

  // --- Render: Manual channel input mode ---
  if (manualMode && activeConnectorId) {
    return (
      <div className="space-y-2">
        {label && (
          <div className="flex items-center justify-between gap-3">
            <label className="field-label block">
              {label}
              {required && <span className="ml-1 text-red-500">*</span>}
            </label>
            {headerRight}
          </div>
        )}
        {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
        {showWorkspaceSelector && renderWorkspaceSelector()}
        <details className="group">
          <summary className="caption-xs text-ds-text-secondary flex cursor-pointer list-none items-center gap-1.5 dark:text-zinc-200 [&::-webkit-details-marker]:hidden">
            <IconChevronRightOutline18 className="text-ds-text-disabled h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90" />
            <span className="font-medium">Using a private channel?</span>
          </summary>
          <div className="mt-1.5 space-y-1.5 pl-5">
            <p className="caption-xs">
              Slack hides private channels from app listings for security — paste the Channel ID manually.
            </p>
            <ol className="caption-xs marker:text-ds-text-disabled list-decimal space-y-0.5 pl-4">
              <li>In Slack, click the channel name.</li>
              <li>Scroll to the bottom of the About panel and copy the Channel ID.</li>
              <li>Invite the bot to the channel — otherwise sends fail.</li>
            </ol>
          </div>
        </details>
        {multiSelect ? (
          <Input
            type="text"
            value={selectedValues.join(", ")}
            onChange={e =>
              onChange(
                e.target.value
                  .split(",")
                  .map(s => s.trim())
                  .filter(Boolean),
              )
            }
            placeholder="C01ABC2DEF3, C04XYZ5GHI6"
            required={required}
            className="text-ds-text-primary"
          />
        ) : (
          <Input
            type="text"
            value={typeof value === "string" ? value : ""}
            onChange={e => onChange(e.target.value)}
            placeholder="C01ABC2DEF3"
            required={required}
            className="text-ds-text-primary"
          />
        )}
        {!fetchError && (
          <button
            type="button"
            onClick={() => setManualMode(false)}
            className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Select from channels
          </button>
        )}
        {fetchError && <p className="caption-xs">Could not load channels. Enter channel ID manually.</p>}
      </div>
    );
  }

  // --- Helper: workspace selector dropdown ---
  function renderWorkspaceSelector() {
    return (
      <div className="relative" ref={workspaceRef}>
        <label className="field-label text-ds-text-muted mb-1 block text-xs">Workspace</label>
        <button
          type="button"
          onClick={() => setWorkspaceDropdownOpen(!workspaceDropdownOpen)}
          className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:border-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:hover:border-zinc-600"
        >
          {activeConnector ? (
            <span className="flex items-center gap-2">
              <IconPlugOutline18 className="h-4 w-4 text-blue-500" />
              <span>{activeConnector.name}</span>
            </span>
          ) : (
            <span className="text-ds-text-disabled dark:text-ds-text-muted">Select a workspace...</span>
          )}
          <IconChevronDownOutline18 className="text-ds-text-disabled h-4 w-4" />
        </button>
        {workspaceDropdownOpen && (
          <div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border">
            {slackConnectors.map(connector => (
              <button
                key={connector.id}
                type="button"
                onClick={() => handleWorkspaceChange(connector.id)}
                className={`hover:bg-ds-surface-hover flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                  connector.id === activeConnectorId
                    ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    : "text-ds-text-primary"
                }`}
              >
                <IconPlugOutline18 className="h-4 w-4 flex-shrink-0 opacity-60" />
                <span className="min-w-0 flex-1 truncate">{connector.name}</span>
                {connector.id === activeConnectorId && (
                  <IconCheckOutline18 className="h-4 w-4 flex-shrink-0 text-blue-500" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- Render: Selected channel tags (multi-select) ---
  const renderSelectedTags = () => {
    if (!multiSelect || selectedValues.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1.5 pb-1">
        {selectedValues.map(id => {
          const ch = findChannel(id);
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
            >
              {ch ? getChannelDisplayName(ch) : `#${id}`}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  handleRemove(id);
                }}
                className="ml-0.5 text-blue-400 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-300"
              >
                &times;
              </button>
            </span>
          );
        })}
      </div>
    );
  };

  // --- Render: Single-select display ---
  const renderSelectedSingle = () => {
    if (multiSelect) return null;
    const selected = selectedValues[0];
    if (!selected) return <span className="text-ds-text-disabled dark:text-ds-text-muted">Select a channel...</span>;

    const ch = findChannel(selected);
    if (ch) {
      return (
        <span className="flex items-center gap-2">
          <span className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-700 rounded px-1.5 py-0.5 text-xs">
            {getChannelTypeLabel(ch)}
          </span>
          <span>{getChannelDisplayName(ch)}</span>
        </span>
      );
    }
    return <span className="text-ds-text-subtle dark:text-ds-text-disabled">Channel: {selected}</span>;
  };

  // --- Render: Read-only text (no dropdowns, just the picked channels + workspace) ---
  if (disabled) {
    // Accept three shapes: bare channel-id strings, pre-formatted text strings
    // (from echoed/completed responses), and the enriched ResolvedSlackSelection
    // objects produced after submission.
    const isChannelId = isSlackId;
    const rawValues: unknown[] = Array.isArray(value) ? value : value ? [value] : [];
    const rendered = rawValues.map(v => {
      if (v && typeof v === "object") {
        const d = v as ResolvedSlackSelection;
        const channel = d.channelName ? `#${d.channelName}` : d.channelId ? `#${d.channelId}` : "";
        return d.workspaceName ? `${channel} in ${d.workspaceName}` : channel;
      }
      if (typeof v === "string" && isChannelId(v)) {
        const ch = findChannel(v);
        return ch ? `#${ch.name}` : `#${v}`;
      }
      // Pre-formatted string from an echoed completed bubble looks like
      // "#general in Acme Workspace (channelId: C08XXX, workspaceId: T08YYY)".
      // Strip the trailing metadata suffix so users only see the friendly part.
      if (typeof v === "string") {
        return stripSlackChannelMetadata(v);
      }
      return String(v);
    });
    const allBareChannelIds = rawValues.length > 0 && rawValues.every(v => typeof v === "string" && isChannelId(v));
    return (
      <div className="space-y-1">
        {label && (
          <label className="field-label block">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        )}
        <div className="text-ds-text-secondary text-sm dark:text-zinc-200">
          {rendered.length > 0 ? (
            rendered.join(", ")
          ) : (
            <span className="text-ds-text-disabled">No channel selected</span>
          )}
          {activeConnector && allBareChannelIds && (
            <span className="text-ds-text-muted ml-2 text-xs">in {activeConnector.name}</span>
          )}
        </div>
      </div>
    );
  }

  // --- Render: Main ---
  return (
    <div className="space-y-2" ref={containerRef}>
      {label && (
        <div className="flex items-center justify-between gap-3">
          <label className="field-label block">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
          {headerRight}
        </div>
      )}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      {showWorkspaceSelector && renderWorkspaceSelector()}
      {!activeConnectorId && showWorkspaceSelector ? (
        <p className="text-ds-text-muted text-xs">Select a workspace to see channels</p>
      ) : (
        <>
          {renderSelectedTags()}

          <div className="relative">
            {activeConnector && slackConnectors.length === 1 && (
              <p className="text-ds-text-muted mb-1 text-xs">
                Workspace: <span className="text-ds-text-secondary font-medium">{activeConnector.name}</span>
              </p>
            )}

            {isOpen ? (
              <Input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search channels..."
                autoFocus
                className="text-ds-text-primary dark:bg-ds-neutral-800 border-blue-500 ring-2 ring-blue-500/20 dark:border-blue-500"
              />
            ) : (
              <button
                type="button"
                onClick={() => setIsOpen(true)}
                className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:border-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:hover:border-zinc-600"
              >
                {multiSelect ? (
                  <span className="text-ds-text-disabled dark:text-ds-text-muted">
                    {selectedValues.length > 0
                      ? `${selectedValues.length} selected — click to add more`
                      : "Select channels..."}
                  </span>
                ) : (
                  renderSelectedSingle()
                )}
                <IconChevronDownOutline18 className="text-ds-text-disabled h-4 w-4" />
              </button>
            )}

            {/* Channel dropdown */}
            {isOpen && (
              <div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border">
                {queryIsChannelId && !channelIdAlreadyInList && (
                  <button
                    type="button"
                    onClick={() => handleSelect(upperQuery)}
                    disabled={lookupLoading}
                    className="border-ds-border-default flex w-full items-center gap-2 border-b px-3 py-2 text-left text-sm hover:bg-blue-50 disabled:opacity-60 dark:hover:bg-blue-900/30"
                  >
                    {renderIdLookupRow({
                      id: upperQuery,
                      lookupLoading,
                      resolvedChannel: resolvedQueryChannel,
                      lookupState,
                      lookupErrorMessage,
                    })}
                  </button>
                )}
                {loading ? (
                  <div className="text-ds-text-disabled flex items-center gap-2 px-3 py-2 text-sm">
                    <PixelLoader size="sm" />
                    Loading channels...
                  </div>
                ) : filteredChannels.length === 0 && !queryIsChannelId ? (
                  <div className="text-ds-text-disabled px-3 py-2 text-sm">
                    {query ? "No channels match your search" : "No channels found"}
                  </div>
                ) : (
                  filteredChannels.map(ch => {
                    const isSelected = selectedValues.includes(ch.id);
                    return (
                      <button
                        key={ch.id}
                        type="button"
                        onClick={() => handleSelect(ch.id)}
                        className={`hover:bg-ds-surface-hover flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                          isSelected
                            ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                            : "text-ds-text-primary"
                        }`}
                      >
                        {multiSelect && (
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                              isSelected
                                ? "border-blue-500 bg-blue-500 text-white"
                                : "border-ds-neutral-300 dark:border-zinc-600"
                            }`}
                          >
                            {isSelected && (
                              <svg
                                className="h-3 w-3"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </span>
                        )}
                        <span className="bg-ds-neutral-100 text-ds-text-subtle dark:bg-ds-neutral-700 shrink-0 rounded px-1.5 py-0.5 text-xs">
                          {getChannelTypeLabel(ch)}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{getChannelDisplayName(ch)}</span>
                        {!ch.is_user && ch.num_members != null && (
                          <span className="caption-xs shrink-0">{ch.num_members} members</span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="space-y-0.5">
            <button
              type="button"
              onClick={() => setManualMode(true)}
              className="text-ds-text-muted hover:text-ds-text-secondary text-xs dark:hover:text-zinc-300"
            >
              Enter channel ID manually
            </button>
            <p className="caption-xs">
              Use this for private channels — Slack hides them from the channel list for security, so you&apos;ll need
              to paste the channel ID from Slack directly.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
